/**
 * Column-visibility store (zustand). Shared between ResultsHeader (the "Колонки ▾"
 * picker) and VolunteersTable (which hides unselected columns from the grid template,
 * header, rows and skeleton). The activist column is pinned (always visible).
 *
 * Visibility persists to localStorage under "mobui-columns" so the choice survives
 * reloads. The checkbox/select column is structural (not user-toggleable) and lives
 * outside this list.
 */

import { create } from "zustand";

/** Toggleable column keys, in display order (matches the grid template after the checkbox). */
export type ColumnKey =
  | "activist"
  | "branch"
  | "phone"
  | "birthday"
  | "readiness"
  | "loyalty"
  | "membership";

export interface ColumnDef {
  key: ColumnKey;
  label: string;
  /** Grid-template track for this column (used to rebuild the template when visible). */
  track: string;
  /** Pinned columns cannot be hidden (the activist identity column). */
  pinned?: boolean;
}

/** Ordered column definitions — the single source of truth for label + grid track. */
export const COLUMNS: ColumnDef[] = [
  { key: "activist", label: "Активист", track: "minmax(0,1.6fr)", pinned: true },
  { key: "branch", label: "Отделение", track: "112px" },
  { key: "phone", label: "Телефон", track: "116px" },
  { key: "birthday", label: "Д.Р.", track: "74px" },
  { key: "readiness", label: "Готовность", track: "104px" },
  { key: "loyalty", label: "Лояльность", track: "100px" },
  { key: "membership", label: "Членство", track: "84px" },
];

/** Leading structural track for the row checkbox (always present, not toggleable). */
const CHECKBOX_TRACK = "32px";

const STORAGE_KEY = "mobui-columns";

type Visibility = Record<ColumnKey, boolean>;

const ALL_VISIBLE: Visibility = COLUMNS.reduce((acc, c) => {
  acc[c.key] = true;
  return acc;
}, {} as Visibility);

/** Read persisted visibility, merging onto the defaults; pinned columns stay visible. */
function readStored(): Visibility {
  const base: Visibility = { ...ALL_VISIBLE };
  if (typeof window === "undefined") return base;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Record<ColumnKey, boolean>>;
      for (const c of COLUMNS) {
        if (!c.pinned && typeof parsed[c.key] === "boolean") base[c.key] = parsed[c.key] as boolean;
      }
    }
  } catch {
    /* ignore malformed storage */
  }
  for (const c of COLUMNS) if (c.pinned) base[c.key] = true;
  return base;
}

function persist(visibility: Visibility): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visibility));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

interface ColumnsState {
  visibility: Visibility;
  /** Toggle one column (no-op for pinned columns). */
  toggle: (key: ColumnKey) => void;
}

export const useColumns = create<ColumnsState>((set, get) => ({
  visibility: readStored(),
  toggle: (key) => {
    const def = COLUMNS.find((c) => c.key === key);
    if (!def || def.pinned) return;
    const next: Visibility = { ...get().visibility, [key]: !get().visibility[key] };
    persist(next);
    set({ visibility: next });
  },
}));

/** Build the CSS grid-template-columns string from the currently visible columns. */
export function gridTemplate(visibility: Visibility): string {
  const tracks = [CHECKBOX_TRACK];
  for (const c of COLUMNS) if (visibility[c.key]) tracks.push(c.track);
  return tracks.join(" ");
}

/** Ordered list of currently visible toggleable columns (excludes the checkbox track). */
export function visibleColumns(visibility: Visibility): ColumnDef[] {
  return COLUMNS.filter((c) => visibility[c.key]);
}
